import { useCallback, useEffect, useRef, useState } from 'react';
import { loadPayternalScript } from 'utils/loadPayternalScript';

/**
 * Generic hook for integrating the Payternal Web Payments SDK.
 *
 * Usage:
 *   const { sdkReady, sendMessage } = usePayternal({
 *     domId: 'payment-checkout-grn-1234',
 *     paymentInfo: { amount, formattedAmount, buttonLabel },
 *     userInfo: { userId, phone },
 *     getToken: async () => string,
 *     onCheckoutRequest: async (sdkPayload) => ({
 *       order_id, payments_hash, service_type, host_redirect_url, ...
 *     }),
 *     onClose: () => setShowPayment(false),
 *   });
 *   // Render: <div id="payment-checkout-grn-1234" />
 */
export function usePayternal({
  domId,
  paymentInfo,
  userInfo,
  getToken,
  onCheckoutRequest,
  onClose,
  onReady,
  onMessage,
  appearance = { brand: 'zomato', mode: 'light', buttonSize: "lg" },
}) {
  const instanceRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkLoading, setSdkLoading] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  // Stable sendMessage — always reads from instanceRef at call time
  const sendMessage = useCallback((type, payload) => {
    instanceRef.current?.sendMessage({ type, payload });
  }, []);

  const handleTokenRequest = async () => {
    try {
      const token = await getToken();

      sendMessage('TOKEN', { token });
    } catch (err) {
      console.error('[Payternal] getToken failed:', err);
      sendMessage('SHOW_ERROR', { message: 'Failed to load payment. Please try again.' });
    }
  };

  const handleCheckout = async (sdkPayload) => {
    try {
      const checkoutData = await onCheckoutRequest(sdkPayload);
      console.log('[Payternal] Checkout data prepared:', checkoutData);
      sendMessage('CHECKOUT', checkoutData);
    } catch (err) {
      console.error('[Payternal] onCheckoutRequest failed:', err);
      sendMessage('CANCEL_CHECKOUT', {});
      sendMessage('FORCE_CLOSE', {});
    }
  };

  // Fresh handler every render — kept current via setOnReceiveMessage below
  const onReceiveMessage = ({ type, payload }) => {
    const EVENTS = window.Payternal?.EVENTS ?? {};

    console.log('[Payternal] Received message:', { type, payload });
    switch (type) {
      case EVENTS.TOKEN:
        handleTokenRequest();
        break;
      case EVENTS.READY:
        setSdkReady(true);
        setSdkLoading(false);
        onReady?.();
        break;
      case EVENTS.CHECKOUT:
        handleCheckout(payload);
        break;
      case EVENTS.CLOSE:
        onClose?.();
        break;
      default:
        onMessage?.(type, payload);
        break;
    }
  };

  // Init SDK once per domId
  useEffect(() => {
    setSdkReady(false);
    setSdkLoading(true);
    setSdkError(null);

    let cancelled = false;

    loadPayternalScript()
      .then(() => {
        if (cancelled || !window.Payternal) throw new Error('Payternal SDK not loaded');

        console.log('[Payternal] Initializing SDK with:', { domId, paymentInfo, userInfo, appearance });
        return window.Payternal.init({
          domId,
          paymentInfo,
          userInfo,
          appearance,
          onReceiveMessage,
        });
      })
      .then((instance) => {
        if (cancelled) { instance?.destroy?.(); return; }
        instanceRef.current = instance;
      })
      .catch((err) => {
        if (!cancelled) {
          setSdkError(err?.message ?? 'SDK load failed');
          setSdkLoading(false);
        }
      });

    return () => {
      cancelled = true;
      instanceRef.current?.destroy?.();
      instanceRef.current = null;
      setSdkReady(false);
    };
  }, [domId]); 

  // Keep the handler fresh when paymentInfo changes (mirrors the reference impl)
  useEffect(() => {
    if (!instanceRef.current) return;

    instanceRef.current.setOnReceiveMessage(onReceiveMessage);

    sendMessage('UPDATE_PAYMENT_INFO', {
      amount: paymentInfo?.amount,
      formattedAmount: paymentInfo?.formattedAmount,
      buttonLabel: paymentInfo?.buttonLabel,
    });
  }, [paymentInfo?.amount]); 

  return { sdkReady, sdkLoading, sdkError, sendMessage };
}
